import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { AuctionGrid } from "@/components/auction-grid";
import { HomeHero } from "@/components/home-hero";
import { getFaqItems } from "@/lib/home-faq";
import { translate, detectLocale } from "@/i18n";
import { SponsorBannerSlot } from "@/components/sponsor-banner";

// Below-the-fold: lazy-loaded to reduce initial JS and TBT.
const PartnerCarousel = lazy(() =>
  import("@/components/partner-carousel").then((m) => ({ default: m.PartnerCarousel })),
);
const SeoIntroSection = lazy(() =>
  import("@/components/home-seo-sections").then((m) => ({ default: m.SeoIntroSection })),
);
const CategoryGridPremium = lazy(() =>
  import("@/components/home-seo-sections").then((m) => ({ default: m.CategoryGridPremium })),
);
const BuyerTrustSection = lazy(() =>
  import("@/components/home-seo-sections").then((m) => ({ default: m.BuyerTrustSection })),
);
const FeaturedShopsShowcase = lazy(() =>
  import("@/components/featured-shops-showcase").then((m) => ({
    default: m.FeaturedShopsShowcase,
  })),
);
const BecomePartnerCta = lazy(() =>
  import("@/components/become-partner-cta").then((m) => ({ default: m.BecomePartnerCta })),
);
const CommunityUpdatesSection = lazy(() =>
  import("@/components/community-updates-section").then((m) => ({
    default: m.CommunityUpdatesSection,
  })),
);
const UpcomingEventsSection = lazy(() =>
  import("@/components/upcoming-events-section").then((m) => ({
    default: m.UpcomingEventsSection,
  })),
);
const NewsletterSignup = lazy(() =>
  import("@/components/newsletter-signup").then((m) => ({ default: m.NewsletterSignup })),
);
const SellerCtaSection = lazy(() =>
  import("@/components/home-seo-sections").then((m) => ({ default: m.SellerCtaSection })),
);
const CollectionTypesSection = lazy(() =>
  import("@/components/home-seo-sections").then((m) => ({ default: m.CollectionTypesSection })),
);
const FaqSection = lazy(() =>
  import("@/components/home-seo-sections").then((m) => ({ default: m.FaqSection })),
);

// Workstream C: recommendation engine widgets, lazily loaded below the fold.
const RecommendedForYouWidget = lazy(() =>
  import("@/components/recommendations").then((m) => ({ default: m.RecommendedForYouWidget })),
);
const TrendingCardsWidget = lazy(() =>
  import("@/components/recommendations").then((m) => ({ default: m.TrendingCardsWidget })),
);
const PopularSellersWidget = lazy(() =>
  import("@/components/recommendations").then((m) => ({ default: m.PopularSellersWidget })),
);
import {
  fetchEndingTonight,
  fetchFeatured,
  fetchRecentlyApproved,
  fetchHighValue,
  fetchLowStartingBid,
  fetchMostWatched,
  fetchPsa10,
  fetchBgsBlackLabel,
  fetchJapanese,
  fetchByKeyword,
  fetchVintageGrails,
  fetchEndingSoon,
  fetchNewlyListed,
  fetchBuyNowActive,
  fetchFeaturedPromoted,
} from "@/lib/auctions";
import { socialImageMeta } from "@/lib/seo";

const HOME_TITLE = "BidNova Auctions UK | Trading Cards, Collectibles & Fashion Auctions";
const HOME_DESCRIPTION =
  "Bid More. Collect More. The UK’s auction marketplace for trading cards, Funko Pop!, figures, collectibles, streetwear and sneakers — secure Stripe payments and tracked UK shipping.";
const HOME_KEYWORDS =
  "funko pop uk, anime figures uk, streetwear auction uk, sneakers auction uk, pokemon cards uk, pokemon auction uk, one piece cards uk, sports cards uk, topps uk, magic the gathering uk, yu-gi-oh uk, dragon ball cards uk, graded cards uk, psa cards uk, bgs cards uk, cgc cards uk, trading card marketplace uk";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: HOME_TITLE },
      { name: "description", content: HOME_DESCRIPTION },
      { name: "keywords", content: HOME_KEYWORDS },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: HOME_TITLE },
      { property: "og:description", content: HOME_DESCRIPTION },
      { property: "og:url", content: "https://bidnovaauctions.uk/" },
      { property: "og:type", content: "website" },
      ...socialImageMeta(),
      { name: "twitter:title", content: HOME_TITLE },
      { name: "twitter:description", content: HOME_DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: "https://bidnovaauctions.uk/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "BidNova Auctions",
          url: "https://bidnovaauctions.uk",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://bidnovaauctions.uk/auctions?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://bidnovaauctions.uk/" },
            {
              "@type": "ListItem",
              position: 2,
              name: "Auctions",
              item: "https://bidnovaauctions.uk/auctions",
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: getFaqItems((key, def) =>
            translate(detectLocale(), "marketing", key, def),
          ).map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Index,
});

type SectionDef = {
  id: string;
  chip: string;
  title: string;
  subtitle: string;
  queryKey: string;
  fetcher: () => Promise<any[]>;
};

type ChipGroup = { label: string; items: SectionDef[] };

const FEATURED: SectionDef[] = [
  {
    id: "featured-promoted",
    chip: "✨ Featured",
    title: "Featured Auctions",
    subtitle: "Promoted by their sellers",
    queryKey: "featured-promoted",
    fetcher: () => fetchFeaturedPromoted(8),
  },
  {
    id: "buy-now-deals",
    chip: "⚡ Buy It Now",
    title: "Buy It Now Deals",
    subtitle: "Skip the bidding — grab it instantly",
    queryKey: "buy-now-deals",
    fetcher: () => fetchBuyNowActive(8),
  },
  {
    id: "ending-tonight",
    chip: "🏆 Ending Tonight",
    title: "Ending Tonight",
    subtitle: "Closing before midnight",
    queryKey: "ending-tonight",
    fetcher: () => fetchEndingTonight(8),
  },
  {
    id: "newly-approved",
    chip: "⭐ Newly Approved",
    title: "Newly Approved",
    subtitle: "Fresh from moderation",
    queryKey: "recently-approved",
    fetcher: () => fetchRecentlyApproved(8),
  },
  {
    id: "most-watched",
    chip: "🔥 Most Watched",
    title: "Most Watched",
    subtitle: "Trending with collectors",
    queryKey: "most-watched",
    fetcher: () => fetchMostWatched(8),
  },
  {
    id: "high-value",
    chip: "💎 High Value (£1,000+)",
    title: "High Value Lots — £1,000+",
    subtitle: "The heavy hitters",
    queryKey: "high-value",
    fetcher: () => fetchHighValue(100000, 8),
  },
  {
    id: "low-start",
    chip: "⚡ Low Starting Bid",
    title: "Low Starting Bid",
    subtitle: "Steals under £20",
    queryKey: "low-start",
    fetcher: () => fetchLowStartingBid(2000, 8),
  },
];

const CATEGORIES: SectionDef[] = [
  {
    id: "cat-pokemon",
    chip: "Pokémon",
    title: "Pokémon",
    subtitle: "Kanto to Paldea",
    queryKey: "cat-pokemon",
    fetcher: () =>
      fetchByKeyword(["pokemon", "pokémon", "charizard", "pikachu", "scarlet", "violet"], 8),
  },
  {
    id: "cat-one-piece",
    chip: "One Piece",
    title: "One Piece TCG",
    subtitle: "Romance Dawn & beyond",
    queryKey: "cat-one-piece",
    fetcher: () => fetchByKeyword(["one piece", "op-", "romance dawn", "paramount war"], 8),
  },
  {
    id: "cat-sports",
    chip: "Sports Cards",
    title: "Sports Cards",
    subtitle: "Football, basketball & more",
    queryKey: "cat-sports",
    fetcher: () =>
      fetchByKeyword(
        [
          "topps",
          "panini",
          "prizm",
          "select",
          "donruss",
          "mosaic",
          "upper deck",
          "bowman",
          "football",
          "basketball",
          "baseball",
        ],
        8,
      ),
  },
  {
    id: "cat-yugioh",
    chip: "Yu-Gi-Oh!",
    title: "Yu-Gi-Oh!",
    subtitle: "It's time to duel",
    queryKey: "cat-yugioh",
    fetcher: () =>
      fetchByKeyword(["yu-gi-oh", "yugioh", "yu gi oh", "blue-eyes", "dark magician"], 8),
  },
  {
    id: "cat-magic",
    chip: "Magic",
    title: "Magic: The Gathering",
    subtitle: "From Alpha to Modern Horizons",
    queryKey: "cat-magic",
    fetcher: () =>
      fetchByKeyword(
        ["magic the gathering", "mtg", "black lotus", "modern horizons", "commander"],
        8,
      ),
  },
  {
    id: "cat-lorcana",
    chip: "Disney Lorcana",
    title: "Disney Lorcana",
    subtitle: "Illumineer favourites",
    queryKey: "cat-lorcana",
    fetcher: () =>
      fetchByKeyword(
        ["lorcana", "disney lorcana", "into the inklands", "rise of the floodborn"],
        8,
      ),
  },
];

const POKEMON: SectionDef[] = [
  {
    id: "pk-psa10",
    chip: "PSA 10",
    title: "Pokémon — PSA 10",
    subtitle: "Gem mint only",
    queryKey: "pk-psa10",
    fetcher: () => fetchPsa10(8),
  },
  {
    id: "pk-bgs-black",
    chip: "BGS Black Label",
    title: "Pokémon — BGS Black Label",
    subtitle: "Pristine perfection",
    queryKey: "pk-bgs-black",
    fetcher: () => fetchBgsBlackLabel(8),
  },
  {
    id: "pk-japanese",
    chip: "Japanese",
    title: "Pokémon — Japanese",
    subtitle: "Straight from Japan",
    queryKey: "pk-japanese",
    fetcher: () => fetchJapanese(8),
  },
  {
    id: "pk-english",
    chip: "English",
    title: "Pokémon — English",
    subtitle: "WOTC & TPCi prints",
    queryKey: "pk-english",
    fetcher: () =>
      fetchByKeyword(
        [
          "base set",
          "jungle",
          "fossil",
          "evolving skies",
          "obsidian flames",
          "paldea evolved",
          "151",
        ],
        8,
      ),
  },
  {
    id: "pk-vintage",
    chip: "Vintage",
    title: "Pokémon — Vintage",
    subtitle: "Base Set era & earlier",
    queryKey: "pk-vintage",
    fetcher: () => fetchVintageGrails(8),
  },
  {
    id: "pk-modern",
    chip: "Modern",
    title: "Pokémon — Modern",
    subtitle: "Sword, Scarlet & beyond",
    queryKey: "pk-modern",
    fetcher: () =>
      fetchByKeyword(
        [
          "sword",
          "shield",
          "scarlet",
          "violet",
          "sun",
          "moon",
          "evolving",
          "obsidian",
          "paldea",
          "crown zenith",
          "151",
          "surging",
        ],
        8,
      ),
  },
  {
    id: "pk-sealed",
    chip: "Sealed Products",
    title: "Pokémon — Sealed Products",
    subtitle: "Booster boxes, ETBs & tins",
    queryKey: "pk-sealed",
    fetcher: () =>
      fetchByKeyword(["sealed", "booster box", "etb", "elite trainer", "booster bundle", "tin"], 8),
  },
];

const GROUPS: ChipGroup[] = [
  { label: "Featured Auctions", items: FEATURED },
  { label: "Categories", items: CATEGORIES },
  { label: "Pokémon", items: POKEMON },
];

const FEED: SectionDef[] = [
  {
    id: "feed-ending-soon",
    chip: "Ending Soon",
    title: "Ending Soon",
    subtitle: "Grab them before the gavel drops",
    queryKey: "feed-ending-soon",
    fetcher: () => fetchEndingSoon(8),
  },
  {
    id: "feed-recent",
    chip: "Recently Listed",
    title: "Recently Listed",
    subtitle: "The latest additions",
    queryKey: "feed-recent",
    fetcher: () => fetchNewlyListed(8),
  },
  {
    id: "feed-featured",
    chip: "Featured Auctions",
    title: "Featured Auctions",
    subtitle: "Handpicked by our team",
    queryKey: "feed-featured",
    fetcher: () => fetchFeatured(8),
  },
];

function useNearViewport<T extends HTMLElement>(rootMargin = "400px") {
  const ref = useRef<T | null>(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    if (!ref.current || near) return;
    if (typeof IntersectionObserver === "undefined") {
      setNear(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setNear(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin },
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [near, rootMargin]);
  return { ref, near };
}

function Section({ def }: { def: SectionDef }) {
  const { ref, near } = useNearViewport<HTMLElement>();
  const { data, isLoading } = useQuery({
    queryKey: [def.queryKey],
    queryFn: def.fetcher,
    refetchInterval: 30000,
    enabled: near,
  });
  return (
    <section ref={ref} id={def.id} className="mx-auto max-w-7xl px-6 sm:px-8 py-14 scroll-mt-28">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <div className="text-primary text-[11px] uppercase tracking-[0.2em]">{def.subtitle}</div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl text-white">{def.title}</h2>
        </div>
        <Link
          to="/auctions"
          className="text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          View all →
        </Link>
      </div>
      {!near || isLoading ? (
        <div className="grid gap-5 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="rounded-2xl h-[380px] skeleton-shimmer border border-white/10"
            />
          ))}
        </div>
      ) : (
        <AuctionGrid auctions={data ?? []} emptyText="No live auctions in this category yet." />
      )}
    </section>
  );
}

function ChipBar() {
  return (
    <div className="sticky top-16 z-30 border-y border-border/60 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-3 space-y-2">
        {GROUPS.map((group) => (
          <div key={group.label} className="flex items-center gap-3">
            <span className="shrink-0 text-[11px] uppercase tracking-widest text-muted-foreground/80 w-28 hidden md:block">
              {group.label}
            </span>
            <div className="flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {group.items.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-gold/25 bg-card/60 px-3.5 py-1.5 text-xs uppercase tracking-widest text-muted-foreground hover:border-gold/60 hover:text-gold transition-colors"
                >
                  {s.chip}
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LazyBlock({
  children,
  minHeight = 320,
}: {
  children: React.ReactNode;
  minHeight?: number;
}) {
  return <Suspense fallback={<div style={{ minHeight }} aria-hidden />}>{children}</Suspense>;
}

function Index() {
  return (
    <div className="relative overflow-hidden">
      <HomeHero />

      <SponsorBannerSlot placement="homepage_hero" variant="hero" className="mt-10 mb-5" />

      <LazyBlock minHeight={120}>
        <PartnerCarousel />
      </LazyBlock>

      <LazyBlock minHeight={320}>
        <SeoIntroSection />
      </LazyBlock>

      <LazyBlock minHeight={480}>
        <FeaturedShopsShowcase />
      </LazyBlock>

      <LazyBlock minHeight={520}>
        <CategoryGridPremium />
      </LazyBlock>

      <LazyBlock minHeight={320}>
        <BuyerTrustSection />
      </LazyBlock>

      <ChipBar />

      {FEED.map((s) => (
        <Section key={s.id} def={s} />
      ))}

      {FEATURED.map((s) => (
        <Section key={s.id} def={s} />
      ))}
      {CATEGORIES.map((s) => (
        <Section key={s.id} def={s} />
      ))}
      {POKEMON.map((s) => (
        <Section key={s.id} def={s} />
      ))}

      <div className="mx-auto max-w-7xl px-6 sm:px-8">
        <LazyBlock minHeight={420}>
          <RecommendedForYouWidget />
        </LazyBlock>

        <LazyBlock minHeight={420}>
          <TrendingCardsWidget />
        </LazyBlock>

        <LazyBlock minHeight={420}>
          <PopularSellersWidget />
        </LazyBlock>
      </div>

      <LazyBlock>
        <SellerCtaSection />
      </LazyBlock>

      <LazyBlock>
        <CollectionTypesSection />
      </LazyBlock>

      <LazyBlock>
        <BecomePartnerCta />
      </LazyBlock>

      <LazyBlock>
        <UpcomingEventsSection />
      </LazyBlock>

      <LazyBlock>
        <CommunityUpdatesSection />
      </LazyBlock>

      <LazyBlock minHeight={240}>
        <NewsletterSignup />
      </LazyBlock>

      <LazyBlock>
        <FaqSection />
      </LazyBlock>
    </div>
  );
}
